import { Component, OnInit, inject } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzQRCodeModule } from 'ng-zorro-antd/qr-code';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { BrandingService } from '@services/branding.service';
import { PagesService } from '@services/pages.service';
import { SharedService } from '@services/shared.service';
import { FilterConfig, FilterModalComponent } from '../../filter-modal/filter-modal.component';
import {
  DEFAULT_QR_BASE,
  DEFAULT_QR_DOMAIN,
  QR_BASE_STORAGE_PREFIX,
  QR_DOMAIN_PATH,
  QrDomainOption,
  composeQrShortLink,
} from 'src/app/utils/web-qr-link.util';

// Destinations, link composition and the per-code host choice all live in
// web-qr-link.util, because the campaign detail page renders the same QR and a
// second copy of the default host would eventually disagree with this one.

const QR_PREVIEW_SIZE = 220;
const QR_PREVIEW_ICON_SIZE = 60;
const QR_LOGO_SRC = 'assets/images/luqta-qr-logo.png';

// nz-qrcode sizes its canvas from the module count (~470px here), not from
// nzSize — nzSize is only the CSS display size. 470px is ~1.5" at 300 DPI, too
// coarse for the "print" placement type, so downloads are upscaled to at least
// this width before being saved.
const QR_DOWNLOAD_MIN_WIDTH = 1500;

interface QrInstance {
  id?: number | string;
  slug?: string;
  label?: string;
  placement_type?: string;
  is_active?: boolean;
  expires_at?: string | number | null;
  activated_at?: string | number | null;
  scan_count?: number;
  notes?: string;
  created_at?: string | number | null;
  short_link?: string;
  qr_value?: string;
  base_url?: string;
  /** How many may join through this code. Null/0 = no limit. */
  max_participants?: number | null;
  /** Joined through this code so far. */
  joined_here?: number;
  /** Null when the code is uncapped — unlimited, not unknown. */
  remaining?: number | null;
  is_full?: boolean;
}

/**
 * How a contest's participant allowance is spread across its codes.
 *
 * Only active codes reserve, and an uncapped one reserves nothing while still
 * being able to absorb the rest — so `unallocated` is a guide rather than a
 * guarantee whenever `uncapped_codes` is non-zero.
 */
interface QrAllocation {
  /** Null = the contest sets no participant limit of its own. */
  contest_limit: number | null;
  reserved: number;
  unallocated: number | null;
  uncapped_codes: number;
}

@Component({
  selector: 'app-web-qr',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    NzDatePickerModule,
    NzDrawerModule,
    NzModalModule,
    NzQRCodeModule,
    NzSelectModule,
    NzTableModule,
    NzToolTipModule,
    FilterModalComponent,
  ],
  templateUrl: './web-qr.component.html',
  styleUrls: ['./web-qr.component.scss'],
})
export class WebQrComponent implements OnInit {
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private location = inject(Location);
  private pagesService = inject(PagesService);
  private brandingService = inject(BrandingService);
  private translate = inject(TranslateService);
  sharedService = inject(SharedService);

  contestId = 0;
  contestName = '';
  qrInstances: QrInstance[] = [];
  isSaving = false;
  isModalVisible = false;
  submitted = false;
  qrForm!: FormGroup;

  // Set while the modal is editing an existing instance; null while creating.
  editingInstance: QrInstance | null = null;
  /**
   * An instance the URL asked to edit (?qr_instance_id=). The campaign detail
   * page's QR table links straight here, and the form cannot open until the
   * list — and the allocation its participant cap depends on — has loaded.
   * Cleared once used, so a later reload does not reopen the form.
   */
  private pendingEditInstanceId = 0;

  // Preview modal for a single QR instance.
  previewInstance: QrInstance | null = null;
  previewLinkCopied = false;
  private previewLinkCopiedTimer: any = null;

  // ── Participants Drawer ──
  isParticipantsDrawerVisible = false;
  selectedInstanceForParticipants: QrInstance | null = null;
  participantsList: any[] = [];
  participantsTotal = 0;
  participantsPage = 1;
  participantsPerPage = 20;
  isParticipantsLoading = false;

  qrDomainOptions: QrDomainOption[] = [DEFAULT_QR_DOMAIN];
  // slug -> chosen base, for this contest only.
  private baseOverrides: Record<string, string> = {};
  // Base chosen for a code whose slug the create response did not reveal; claimed
  // by the newest unmapped instance on the reload that follows.
  private pendingBase: string | null = null;

  qrPreviewSize = QR_PREVIEW_SIZE;
  qrPreviewIconSize = QR_PREVIEW_ICON_SIZE;
  qrLogoSrc = QR_LOGO_SRC;

  // ── Scan report tab ──
  activeTab: 'instances' | 'report' = 'instances';

  /**
   * The contest's participant budget and what is left of it.
   *
   * Read so the cap field can offer the right maximum instead of letting the
   * client guess a number the API will refuse.
   */
  allocation: QrAllocation | null = null;
  reportRows: any[] = [];
  reportSummary: any = null;
  reportTotal = 0;
  reportPage = 1;
  reportPerPage = 20;
  reportName = '';
  filterModalVisible = false;
  currentFilters: any = {};

  // The report endpoint only filters on a date range, so every other section of
  // the shared modal is switched off — they default to shown.
  reportFilterConfig: FilterConfig = {
    showTimeline: true,
    showStatus: false,
    showFormat: false,
    showType: false,
    showViewType: false,
    showLocation: false,
    showLevel: false,
  };

  switchTab(tab: 'instances' | 'report'): void {
    this.activeTab = tab;
    // Load lazily: the report is a separate query and most visits only want the
    // instance list.
    if (tab === 'report' && !this.reportRows.length && this.contestId) {
      this.loadReport();
    }
  }

  // ── Participants Drawer Actions ──
  openParticipantsDrawer(instance: QrInstance): void {
    this.selectedInstanceForParticipants = instance;
    this.isParticipantsDrawerVisible = true;
    this.participantsPage = 1;
    this.loadParticipants();
  }

  closeParticipantsDrawer(): void {
    this.isParticipantsDrawerVisible = false;
    this.selectedInstanceForParticipants = null;
    this.participantsList = [];
  }

  loadParticipants(page: number = this.participantsPage): void {
    if (!this.selectedInstanceForParticipants?.id) {
      return;
    }
    this.participantsPage = page;
    this.isParticipantsLoading = true;
    this.sharedService.showLoader();
    const params = {
      qr_instance_id: this.selectedInstanceForParticipants.id,
      page: this.participantsPage,
      per_page: this.participantsPerPage,
    };

    this.pagesService.getAllById('WEB_QR_INSTANCE_PARTICIPANTS', params).subscribe({
      next: (res: any) => {
        this.isParticipantsLoading = false;
        this.sharedService.hideLoader();
        if (res && res.participants && Array.isArray(res.participants)) {
          this.participantsList = res.participants;
          this.participantsTotal = Number(res.total_participants ?? res.participants.length) || 0;
          if (res.instance) {
            this.selectedInstanceForParticipants = {
              ...this.selectedInstanceForParticipants,
              ...res.instance
            };
          }
        } else if (Array.isArray(res)) {
          this.participantsList = res;
          this.participantsTotal = res.length;
        } else if (res?.items && Array.isArray(res.items)) {
          this.participantsList = res.items;
          this.participantsTotal = Number(res?.total ?? res.items.length) || 0;
        } else if (res?.data && Array.isArray(res.data)) {
          this.participantsList = res.data;
          this.participantsTotal = Number(res?.total ?? res.data.length) || 0;
        } else {
          this.participantsList = [];
          this.participantsTotal = 0;
        }
      },
      error: (err: any) => {
        this.isParticipantsLoading = false;
        this.sharedService.hideLoader();
        this.participantsList = [];
        this.participantsTotal = 0;
        this.sharedService.createNotification('error', err?.error?.message || 'Failed to load participants');
      }
    });
  }

  formatParticipantStatus(status: string | null): string {
    if (!status) return 'In Progress';
    return status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  }

  onParticipantsPageChange(page: number): void {
    this.loadParticipants(page);
  }

  onParticipantsPageSizeChange(size: number): void {
    this.participantsPerPage = size;
    this.loadParticipants(1);
  }

  loadReport(resetPage = false): void {
    if (!this.contestId) {
      return;
    }
    if (resetPage) {
      this.reportPage = 1;
    }
    const [from, to] = this.currentFilters?.dateRange || [];
    this.sharedService.showLoader();
    this.pagesService
      .getAllById('WEB_QR_REPORT', {
        contest_id: this.contestId,
        name: (this.reportName || '').trim(),
        // The API defaults these to empty strings, so send '' rather than
        // omitting them when no range is picked.
        start_date: from ? new Date(from).toISOString() : '',
        end_date: to ? new Date(to).toISOString() : '',
        page: this.reportPage,
        per_page: this.reportPerPage,
      })
      .subscribe({
        next: (res: any) => {
          this.reportRows = this.normalizeReport(res);
          this.reportSummary = res?.summary ?? null;
          // The API paginates engagements, not placements, so the page count
          // comes from `total` while the table lists every placement on the page.
          this.reportTotal = Number(res?.total ?? this.reportRows.length) || 0;
          this.sharedService.hideLoader();
        },
        error: () => {
          this.reportRows = [];
          this.reportSummary = null;
          this.reportTotal = 0;
          this.sharedService.hideLoader();
        },
      });
  }

  // The endpoint returns { summary, items: [engagement] } where the per-QR
  // numbers live one level down in `items[].placements[]`. One row per
  // placement is what the table shows, so flatten and carry the engagement
  // name down for context.
  private normalizeReport(res: any): any[] {
    const items = Array.isArray(res?.items) ? res.items : Array.isArray(res) ? res : [];

    return items.flatMap((engagement: any) => {
      const placements = Array.isArray(engagement?.placements) ? engagement.placements : [];
      return placements.map((placement: any) => ({
        ...placement,
        engagement_id: engagement?.id,
        engagement_name: engagement?.name,
        scans: Number(placement?.total_scans ?? 0) || 0,
      }));
    });
  }

  onReportPageChange(page: number): void {
    this.reportPage = page;
    this.loadReport();
  }

  onReportPageSizeChange(size: number): void {
    this.reportPerPage = size;
    this.reportPage = 1;
    this.loadReport();
  }

  openFilterModal(): void {
    this.filterModalVisible = true;
  }

  onFiltersApplied(filters: any): void {
    this.currentFilters = filters;
    this.loadReport(true);
  }

  onFiltersReset(): void {
    this.currentFilters = {};
    this.loadReport(true);
  }

  hasActiveFilters(): boolean {
    return Object.keys(this.currentFilters || {}).some((key) => {
      const value = this.currentFilters[key];
      return Array.isArray(value) ? value.length > 0 : value !== null && value !== '' && value !== undefined;
    });
  }

  // The dot on the filter button clears the search box too, so the table goes
  // back to a fully unfiltered view in one click.
  clearAllFilters(): void {
    this.currentFilters = {};
    this.reportName = '';
    this.loadReport(true);
  }

  outcome(row: any, key: string): number {
    return Number(row?.scans_by_outcome?.[key] ?? row?.[key] ?? 0) || 0;
  }

  // Rates come back null when the denominator is zero (no scans reached the
  // engagement yet) — that is "not measurable", not 0%.
  ratePct(value: any): string {
    return value === null || value === undefined ? '—' : `${Number(value) || 0}%`;
  }

  ngOnInit(): void {
    this.qrForm = this.fb.group({
      label: ['', [Validators.required, Validators.maxLength(100)]],
      placement_type: ['print', Validators.required],
      base_url: [DEFAULT_QR_BASE, Validators.required],
      is_active: [true],
      expires_at: [null],
      // Blank means no limit. Validators.min(1) rather than min(0) because 0 is
      // read as "no limit" by the API too — sending it as a cap would be a
      // confusing way to say the same thing.
      max_participants: [null, [Validators.min(1)]],
      notes: ['', Validators.maxLength(500)],
    });

    this.route.queryParams.subscribe((params) => {
      this.contestId = Number(params['contest_id']) || 0;
      this.contestName = params['contest_name'] || '';
      this.pendingEditInstanceId = Number(params['qr_instance_id']) || 0;
      if (this.contestId) {
        this.loadBaseOverrides();
        this.loadQrInstances();
      }
    });

    this.loadDomainOptions();
  }

  /**
   * Appends the client's registered domains to the hardcoded default.
   *
   * Only live domains are offered — a pending or failed host does not resolve
   * yet, so a QR printed against it would lead nowhere. A failure here is not
   * surfaced: the default destination still works, so the modal stays usable.
   */
  private loadDomainOptions(): void {
    this.brandingService.getDomains().subscribe({
      next: (res: any) => {
        const items: any[] = Array.isArray(res)
          ? res
          : (res?.items || res?.data?.items || res?.data || res?.result || []);
        const seen = new Set([DEFAULT_QR_DOMAIN.base]);
        const options: QrDomainOption[] = [DEFAULT_QR_DOMAIN];

        (items || [])
          .filter((d) => String(d?.status || '').toLowerCase() === 'live')
          .forEach((d) => {
            const host = String(d?.canonical_host || d?.hostname || '')
              .trim()
              .replace(/^https?:\/\//i, '')
              .replace(/\/+$/, '');
            if (!host) return;
            const base = `https://${host}${QR_DOMAIN_PATH}`;
            if (seen.has(base)) return;
            seen.add(base);
            options.push({ label: host, base });
          });

        this.qrDomainOptions = options;
      },
      error: (err) => console.error('Failed to load QR destination domains:', err),
    });
  }

  /** The link the selected domain produces, for the modal's hint line. */
  get qrLinkPreview(): string {
    const base = this.qrForm?.get('base_url')?.value || DEFAULT_QR_BASE;
    const slug = this.editingInstance?.slug;
    return slug ? this.composeShortLink(slug, base) : `${String(base).replace(/\/+$/, '')}/…`;
  }

  goBack(): void {
    this.location.back();
  }

  loadQrInstances(): void {
    this.sharedService.showLoader();
    this.pagesService.getAllById('QR_INSTANCE_LIST', { contest_id: this.contestId }).subscribe({
      next: (res: any) => {
        this.claimPendingBase(res);
        this.qrInstances = this.normalizeList(res);
        this.allocation = this.readAllocation(res);
        this.sharedService.hideLoader();
        this.openPendingEditInstance();
      },
      error: () => {
        this.qrInstances = [];
        this.allocation = null;
        this.sharedService.hideLoader();
      },
    });
  }

  // The endpoint returns { contest_id, total, instances: [...] }.
  private normalizeList(res: any): QrInstance[] {
    const raw = this.readInstances(res);

    return raw.map((item: any) => {
      const slug = item.slug || '';
      const chosenBase = slug ? this.baseOverrides[slug] : '';
      // A chosen domain has to win over `short_link`: the backend composes that
      // field on the default host, so honouring it would ignore the selection.
      const link = chosenBase
        ? this.composeShortLink(slug, chosenBase)
        : item.short_link || item.link || item.url || this.composeShortLink(slug, DEFAULT_QR_BASE);
      // 0 reads as "no limit" the same way null does, so the two are collapsed
      // here rather than in every template expression.
      const cap = Number(item.max_participants) > 0 ? Number(item.max_participants) : null;
      const joined = Number(item.joined_here) || 0;

      return {
        ...item,
        scan_count: item.scan_count ?? 0,
        base_url: chosenBase || DEFAULT_QR_BASE,
        short_link: link,
        qr_value: link,
        max_participants: cap,
        joined_here: joined,
        // Null means unlimited, not unknown — the template has to tell them
        // apart to avoid rendering "0 left" on an uncapped code.
        remaining: cap === null ? null : Math.max(0, cap - joined),
        is_full: cap !== null && joined >= cap,
      };
    });
  }

  /**
   * The contest-wide budget, as the list endpoint reports it.
   *
   * Absent on an older backend, in which case the cap field simply offers no
   * maximum and the API stays the thing that enforces it.
   */
  private readAllocation(res: any): QrAllocation | null {
    const a = res?.allocation;
    if (!a) return null;

    const limit = Number(a.contest_limit) > 0 ? Number(a.contest_limit) : null;
    return {
      contest_limit: limit,
      reserved: Number(a.reserved) || 0,
      unallocated: limit === null ? null : Math.max(0, Number(a.unallocated) || 0),
      uncapped_codes: Number(a.uncapped_codes) || 0,
    };
  }

  private readInstances(res: any): any[] {
    return Array.isArray(res?.instances) ? res.instances : Array.isArray(res) ? res : [];
  }

  /** Joins base and slug without doubling or dropping the separating slash. */
  private composeShortLink(slug: string, base: string): string {
    return composeQrShortLink(slug, base);
  }

  // ── Per-code domain choice ────────────────────────────────────────────────
  private storageKey(): string {
    return `${QR_BASE_STORAGE_PREFIX}${this.contestId}`;
  }

  private loadBaseOverrides(): void {
    try {
      const raw = localStorage.getItem(this.storageKey());
      const parsed = raw ? JSON.parse(raw) : null;
      this.baseOverrides = parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      this.baseOverrides = {};
    }
  }

  private setBaseOverride(slug: string, base: string): void {
    if (!slug) {
      return;
    }
    this.baseOverrides = { ...this.baseOverrides, [slug]: base };
    try {
      localStorage.setItem(this.storageKey(), JSON.stringify(this.baseOverrides));
    } catch {
      // A full or blocked storage only costs the choice its persistence; the
      // in-memory map still drives this session.
    }
  }

  private extractSlug(res: any): string {
    return res?.slug || res?.instance?.slug || res?.data?.slug || '';
  }

  // Only reached when the create response withheld the slug: the code that was
  // just added is the newest one still without a domain of its own.
  private claimPendingBase(res: any): void {
    if (!this.pendingBase) {
      return;
    }
    const unmapped = this.readInstances(res).filter((item: any) => item?.slug && !this.baseOverrides[item.slug]);
    if (unmapped.length) {
      const newest = unmapped.reduce((latest: any, item: any) =>
        new Date(item?.created_at ?? 0).getTime() >= new Date(latest?.created_at ?? 0).getTime() ? item : latest
      );
      this.setBaseOverride(newest.slug, this.pendingBase);
    }
    this.pendingBase = null;
  }

  openCreateModal(): void {
    this.submitted = false;
    this.editingInstance = null;
    this.qrForm.reset({
      label: '',
      placement_type: 'print',
      base_url: DEFAULT_QR_BASE,
      is_active: true,
      expires_at: null,
      max_participants: null,
      notes: '',
    });
    this.qrForm.get('placement_type')?.enable();
    this.isModalVisible = true;
  }

  /**
   * Opens the edit form for the instance named in the URL, if there is one.
   *
   * A missing id is reported rather than ignored: the caller sent the user here
   * to edit something specific, and silently showing the list would look like
   * the click did nothing.
   */
  private openPendingEditInstance(): void {
    const id = this.pendingEditInstanceId;
    if (!id) return;
    this.pendingEditInstanceId = 0;

    const instance = this.qrInstances.find((item) => Number(item.id) === id);
    if (instance) {
      this.openEditModal(instance);
    } else {
      this.sharedService.createNotification('error', this.translate.instant('webQr.instanceNotFound'));
    }
  }

  openEditModal(instance: QrInstance): void {
    this.submitted = false;
    this.editingInstance = instance;
    this.qrForm.reset({
      label: instance.label || '',
      placement_type: instance.placement_type || 'print',
      base_url: instance.base_url || DEFAULT_QR_BASE,
      is_active: instance.is_active !== false,
      expires_at: instance.expires_at ? new Date(instance.expires_at) : null,
      max_participants: instance.max_participants ?? null,
      notes: instance.notes || '',
    });
    // Placement type is fixed once the code is generated — the update endpoint
    // does not accept it, so show it read-only rather than pretending it saves.
    this.qrForm.get('placement_type')?.disable();
    this.isModalVisible = true;
  }

  /**
   * The largest cap the open modal may set, and why.
   *
   * A new code can claim whatever is unallocated. An existing one can also
   * reclaim what it is already holding — it is being replaced in the total, not
   * added to it — which is the same arithmetic the update endpoint does when it
   * excludes the row being edited.
   *
   * Null available means there is nothing to divide up: the contest sets no
   * participant limit of its own.
   */
  get capBudget(): { limit: number | null; available: number | null; uncapped: number } {
    const a = this.allocation;
    if (!a || a.contest_limit === null) {
      return { limit: null, available: null, uncapped: a?.uncapped_codes ?? 0 };
    }

    const own = this.editingInstance?.is_active !== false
      ? (this.editingInstance?.max_participants ?? 0)
      : 0;

    return {
      limit: a.contest_limit,
      available: (a.unallocated ?? 0) + own,
      uncapped: a.uncapped_codes,
    };
  }

  closeCreateModal(): void {
    this.isModalVisible = false;
    this.submitted = false;
    this.editingInstance = null;
    this.qrForm.get('placement_type')?.enable();
  }

  isFieldInvalid(field: string): boolean {
    const control = this.qrForm.get(field);
    return !!(control && control.invalid && (control.dirty || control.touched || this.submitted));
  }

  submitQrInstance(): void {
    this.submitted = true;
    if (this.qrForm.invalid || !this.contestId) {
      Object.values(this.qrForm.controls).forEach((control) => control.markAsTouched());
      return;
    }

    const expiresAt = this.qrForm.get('expires_at')?.value;
    const label = (this.qrForm.get('label')?.value || '').trim();
    const notes = (this.qrForm.get('notes')?.value || '').trim();
    const baseUrl = this.qrForm.get('base_url')?.value || DEFAULT_QR_BASE;
    // Blank, 0 and anything non-numeric all mean "no limit".
    const rawCap = this.qrForm.get('max_participants')?.value;
    const maxParticipants = Number(rawCap) > 0 ? Math.trunc(Number(rawCap)) : null;
    const isEdit = !!this.editingInstance;
    // closeCreateModal() clears editingInstance, so read the slug up front.
    const editingSlug = this.editingInstance?.slug || '';

    const payload = isEdit
      ? {
        id: this.editingInstance!.id,
        label,
        is_active: !!this.qrForm.get('is_active')?.value,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
        // The endpoint needs an explicit flag to drop an existing expiry,
        // since a null date otherwise reads as "leave unchanged".
        clear_expiry: !expiresAt && !!this.editingInstance!.expires_at,
        max_participants: maxParticipants,
        // Same reason as clear_expiry: the endpoint reads a missing cap as
        // "leave alone", so removing one needs saying out loud.
        clear_max_participants:
          maxParticipants === null && this.editingInstance!.max_participants != null,
        notes,
      }
      : {
        contest_id: this.contestId,
        label,
        placement_type: this.qrForm.get('placement_type')?.value || 'print',
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
        max_participants: maxParticipants,
        notes,
        // Ignored by the endpoint today; sent so the domain lands server-side the
        // moment it starts storing one, instead of staying dashboard-only forever.
        base_url: baseUrl,
      };

    this.isSaving = true;
    this.sharedService.showLoader();
    const request$ = isEdit
      ? this.pagesService.create('QR_INSTANCE_UPDATE', payload)
      : this.pagesService.create('QR_INSTANCE_CREATE', payload);

    request$.subscribe({
      next: (res: any) => {
        if (isEdit) {
          this.setBaseOverride(editingSlug, baseUrl);
        } else {
          const createdSlug = this.extractSlug(res);
          if (createdSlug) {
            this.setBaseOverride(createdSlug, baseUrl);
          } else {
            this.pendingBase = baseUrl;
          }
        }
        this.isSaving = false;
        this.sharedService.hideLoader();
        this.sharedService.createNotification(
          'success',
          this.translate.instant(isEdit ? 'webQr.updateSuccess' : 'webQr.createSuccess')
        );
        this.closeCreateModal();
        this.loadQrInstances();
      },
      error: (err: any) => {
        this.isSaving = false;
        this.sharedService.hideLoader();
        this.sharedService.createNotification(
          'error',
          err?.error?.message || this.translate.instant(isEdit ? 'webQr.updateError' : 'webQr.createError')
        );
      },
    });
  }

  openPreview(instance: QrInstance): void {
    this.previewInstance = instance;
    this.previewLinkCopied = false;
  }

  closePreview(): void {
    this.previewInstance = null;
  }

  // The modal can also be dismissed from outside this component: every toast from
  // SharedService.createNotification runs NzModalService.closeAll(), so copying the
  // link tore the modal down while previewInstance stayed set. nzVisible then never
  // went false→true again and the QR icon looked dead until a reload.
  onPreviewVisibleChange(visible: boolean): void {
    if (!visible) {
      this.previewInstance = null;
    }
  }

  async downloadQr(instance: QrInstance | null): Promise<void> {
    if (!instance) {
      return;
    }
    const canvas = document.querySelector<HTMLCanvasElement>('#qrPreviewWrap canvas');
    if (!canvas) {
      this.sharedService.createNotification('error', this.translate.instant('webQr.downloadError'));
      return;
    }

    const anchor = document.createElement('a');
    anchor.href = await this.qrPrintDataUrl(canvas);
    anchor.download = this.qrFileName(instance);
    anchor.click();
  }

  // Upscales the rendered code to print resolution. An integer factor with
  // smoothing off keeps every module a hard square — the same trick would leave
  // the centre logo blocky, so that is repainted from its full-size source.
  private async qrPrintDataUrl(source: HTMLCanvasElement): Promise<string> {
    const factor = Math.max(1, Math.ceil(QR_DOWNLOAD_MIN_WIDTH / source.width));
    if (factor === 1) {
      return source.toDataURL('image/png');
    }

    const target = document.createElement('canvas');
    target.width = source.width * factor;
    target.height = source.height * factor;

    const ctx = target.getContext('2d');
    if (!ctx) {
      return source.toDataURL('image/png');
    }
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(source, 0, 0, target.width, target.height);

    const logo = await this.loadImage(QR_LOGO_SRC);
    if (logo) {
      // Same fraction of the code as the preview, so the printed layout matches.
      const size = (QR_PREVIEW_ICON_SIZE / QR_PREVIEW_SIZE) * target.width;
      const offset = (target.width - size) / 2;
      ctx.imageSmoothingEnabled = true;
      ctx.drawImage(logo, offset, offset, size, size);
    }

    return target.toDataURL('image/png');
  }

  private loadImage(src: string): Promise<HTMLImageElement | null> {
    return new Promise((resolve) => {
      const image = new Image();
      image.onload = () => resolve(image);
      // A missing logo is not worth failing the download over — the upscaled
      // code already carries a (blockier) copy of it.
      image.onerror = () => resolve(null);
      image.src = src;
    });
  }

  // Web share intents open in a browser tab rather than handing off to a desktop
  // app. They can only carry the short link — a URL cannot attach the QR image,
  // so the image stays a download.
  shareQrOn(platform: 'whatsapp' | 'facebook' | 'twitter' | 'linkedin' | 'email' | 'copy'): void {
    const link = this.previewInstance?.short_link || '';
    if (!link) {
      return;
    }

    const name = this.contestName || this.previewInstance?.label || '';
    const message = this.translate.instant('webQr.shareMessage', { name, link });
    const subject = this.translate.instant('webQr.shareSubject', { name });

    switch (platform) {
      case 'whatsapp':
        // web.whatsapp.com rather than wa.me: the latter hands off to the
        // desktop client, which is what we're explicitly avoiding here.
        window.open(`https://web.whatsapp.com/send?text=${encodeURIComponent(message)}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`, '_blank');
        break;
      case 'twitter':
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(subject)}&url=${encodeURIComponent(link)}`,
          '_blank'
        );
        break;
      case 'linkedin':
        window.open(
          `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link)}`,
          '_blank'
        );
        break;
      case 'email':
        window.open(
          `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`,
          '_blank'
        );
        break;
      case 'copy':
        // Also inline — this row lives inside the preview modal.
        this.copyLink(link, true);
        break;
    }
  }

  private qrFileName(instance: QrInstance): string {
    const name = (instance.label || instance.slug || 'qr-code')
      .toString()
      .trim()
      .replace(/[^a-z0-9\-_ ]/gi, '')
      .replace(/\s+/g, '-');
    return `${name || 'qr-code'}.png`;
  }

  // inline = report success on the button itself instead of through a toast.
  // Toasts run NzModalService.closeAll(), which would tear down the preview modal
  // the user copied from.
  copyLink(link: string, inline = false): void {
    if (!link) {
      return;
    }
    navigator.clipboard.writeText(link).then(() => {
      if (!inline) {
        this.sharedService.createNotification('success', this.translate.instant('webQr.linkCopied'));
        return;
      }
      this.previewLinkCopied = true;
      clearTimeout(this.previewLinkCopiedTimer);
      this.previewLinkCopiedTimer = setTimeout(() => (this.previewLinkCopied = false), 2000);
    });
  }

  getPlacementLabel(placement: string | undefined): string {
    return placement === 'digital'
      ? this.translate.instant('webQr.placement.digital')
      : this.translate.instant('webQr.placement.print');
  }

  // Expiry is optional — an instance without one never expires.
  isExpired(instance: QrInstance): boolean {
    if (!instance?.expires_at) {
      return false;
    }
    return new Date(instance.expires_at).getTime() < Date.now();
  }

  // Expired wins over the is_active flag: the code no longer resolves either way.
  getStatusKey(instance: QrInstance): 'expired' | 'active' | 'inactive' {
    if (this.isExpired(instance)) {
      return 'expired';
    }
    return instance?.is_active ? 'active' : 'inactive';
  }
}
